import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Radio, message } from 'antd';
import { DateTime } from 'luxon';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';
import { LoadingIndicator } from 'atoms';
import { WhiteboardErrorCode, useGroupWhiteboards, useLinkWhiteboard } from 'core';
import type { IWhiteboardLessonRef } from 'core';

interface IContinueBoardModal {
  open: boolean;
  lessonId: string;
  groupId: string;
  onClose: () => void;
  onLinked: () => Promise<unknown>;
}

const formatLessonRef = (ref: IWhiteboardLessonRef): string => {
  const parts: string[] = [];
  if (ref.lessonNumber != null) {
    parts.push(`№${ref.lessonNumber}`);
  }
  if (ref.lessonName) {
    parts.push(ref.lessonName);
  }
  if (ref.lessonDate) {
    parts.push(DateTime.fromISO(ref.lessonDate).toFormat('dd.MM.yyyy'));
  }
  return parts.join(' · ');
};

/**
 * "Continue an existing board" picker: boards of the group sorted by
 * updatedAt desc, the most recent preselected; confirming links the chosen
 * board to this lesson.
 */
export const ContinueBoardModal = memo(
  ({ open, lessonId, groupId, onClose, onLinked }: IContinueBoardModal) => {
    const { t } = useTranslation();
    const [selectedId, setSelectedId] = useState<string | null>(null);

    // Deferred until the modal opens (skip) — no picker traffic on tab mount.
    const { whiteboards, loading, error } = useGroupWhiteboards(groupId, !open);
    const { linkWhiteboard, loading: linking } = useLinkWhiteboard();

    useEffect(() => {
      if (!open) {
        setSelectedId(null);
        return;
      }
      if (whiteboards.length && !selectedId) {
        setSelectedId(whiteboards[0].id);
      }
    }, [open, whiteboards, selectedId]);

    const handleConfirm = useCallback(async () => {
      if (!selectedId) {
        return;
      }
      try {
        const result = await linkWhiteboard(lessonId, selectedId);
        if (result.success) {
          onClose();
          await onLinked();
          return;
        }
        if (result.errorCode === WhiteboardErrorCode.LESSON_ALREADY_HAS_BOARD) {
          // Raced with another tab — a board is linked now, just load it.
          onClose();
          await onLinked();
          return;
        }
        message.error(t('whiteboard.errors.linkFailed'));
      } catch {
        message.error(t('whiteboard.errors.linkFailed'));
      }
    }, [selectedId, linkWhiteboard, lessonId, onClose, onLinked, t]);

    return (
      <Modal
        open={open}
        title={t('whiteboard.continueModal.title')}
        okText={t('whiteboard.continueModal.confirm')}
        cancelText={t('whiteboard.continueModal.cancel')}
        okButtonProps={{ disabled: !selectedId || loading, loading: linking }}
        onOk={handleConfirm}
        onCancel={onClose}>
        {loading && <LoadingIndicator />}
        {!loading && Boolean(error) && <EmptyText>{t('whiteboard.errors.loadFailed')}</EmptyText>}
        {!loading && !error && !whiteboards.length && (
          <EmptyText>{t('whiteboard.continueModal.empty')}</EmptyText>
        )}
        {!loading && !error && whiteboards.length > 0 && (
          <BoardsGroup value={selectedId} onChange={(event) => setSelectedId(event.target.value)}>
            {whiteboards.map((board) => (
              <BoardOption key={board.id} value={board.id}>
                <BoardOptionContent>
                  <BoardOptionName>{board.name || t('whiteboard.unnamed')}</BoardOptionName>
                  {board.lessons.map((lessonRef) => (
                    <BoardOptionLesson key={lessonRef.lessonId}>
                      {formatLessonRef(lessonRef)}
                    </BoardOptionLesson>
                  ))}
                  <BoardOptionUpdated>
                    {t('whiteboard.continueModal.updatedAt', {
                      date: DateTime.fromISO(board.updatedAt).toFormat('dd.MM.yyyy HH:mm'),
                    })}
                  </BoardOptionUpdated>
                </BoardOptionContent>
              </BoardOption>
            ))}
          </BoardsGroup>
        )}
      </Modal>
    );
  },
);

const EmptyText = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
`;

const BoardsGroup = styled(Radio.Group)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-height: 50vh;
  overflow-y: auto;
`;

const BoardOption = styled(Radio)`
  align-items: flex-start;
  padding: 12px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 12px;
  margin-inline-end: 0;
  width: 100%;
`;

const BoardOptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BoardOptionName = styled.span`
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })}
`;

const BoardOptionLesson = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const BoardOptionUpdated = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;
